import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCamera, FaCheckCircle } from 'react-icons/fa';
import '../styles/Login.css';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: '' }));
    if (error) setError('');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) errors.email = "L'e-mail est requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "L'e-mail est invalide";
    if (!formData.password) errors.password = 'Le mot de passe est requis';
    else if (formData.password.length < 6) errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'E-mail ou mot de passe invalide');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Branding Side */}
      <div className="login-branding">
        <div className="branding-content">
          <div className="brand-logo"><FaCamera /></div>
          <h1 className="brand-title">PhotoMatch</h1>
          <p className="brand-subtitle">Retrouvez-vous dans chaque moment grâce à la reconnaissance faciale par IA</p>
          
          <div className="features-list">
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Reconnaissance faciale par IA</span>
            </div>
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Découverte instantanée de photos</span>
            </div>
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Sécurisé et privé</span>
            </div>
          </div>
        </div>
        <div className="branding-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
      </div>

      {/* Form Side */}
      <div className="login-form-container">
        <div className="login-form-wrapper">
          <div className="form-header">
            <h2>Bon retour</h2>
            <p>Connectez-vous pour accéder à votre compte</p>
          </div>

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Adresse e-mail</label>
              <div className={`input-wrapper ${fieldErrors.email ? 'error' : ''}`}>
                <FaEnvelope className="input-icon" />
                <input type="email" id="email" name="email" placeholder="you@example.com" 
                  value={formData.email} onChange={handleChange} disabled={loading} />
              </div>
              {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <div className={`input-wrapper ${fieldErrors.password ? 'error' : ''}`}>
                <FaLock className="input-icon" />
                <input type={showPassword ? 'text' : 'password'} id="password" name="password" 
                  placeholder="Saisissez votre mot de passe" value={formData.password} onChange={handleChange} disabled={loading} />
                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Se souvenir de moi</span>
              </label>
              <Link to="/forgot-password" className="forgot-link">Mot de passe oublié ?</Link>
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? <><span className="spinner"></span>Connexion...</> : 'Se connecter'}
            </button>

            <div className="divider"><span>ou</span></div>

            <div className="form-footer">
              <p>Vous n'avez pas de compte ? <Link to="/register" className="register-link">Créer un compte</Link></p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
